import styled from 'styled-components';

import { colors } from 'styles/theme/colors';

type PatientDemographicsData = {
  name?: string;
  dateOfBirth?: string;
  gender?: string;
};

type Props = {
  demographics: PatientDemographicsData;
};

function calculateAge(dateString: string): number | null {
  if (!dateString) return null;
  const birthDate = new Date(dateString);
  if (isNaN(birthDate.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

function formatDateOfBirth(dateString: string): string {
  if (!dateString) return dateString;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function capitalizeGender(gender: string): string {
  if (!gender) return gender;
  return gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase();
}

export default function PatientDemographics({ demographics }: Props) {
  const { name, dateOfBirth, gender } = demographics;
  const age = dateOfBirth ? calculateAge(dateOfBirth) : null;
  const hasDOB = !!dateOfBirth;
  const hasGender = !!gender;
  const showDetailsRow = hasDOB || hasGender;

  return (
    <Container>
      {name && <PatientName>{name}</PatientName>}
      {showDetailsRow && (
        <DetailsRow>
          {hasDOB && (
            <DetailItem>
              <DetailLabel>DOB</DetailLabel>
              <DetailValue>
                {formatDateOfBirth(dateOfBirth!)}
                {age !== null && <Age> (age: {age})</Age>}
              </DetailValue>
            </DetailItem>
          )}
          {hasDOB && hasGender && <Divider />}
          {hasGender && (
            <DetailItem>
              <DetailLabel>Gender</DetailLabel>
              <DetailValue>{capitalizeGender(gender!)}</DetailValue>
            </DetailItem>
          )}
        </DetailsRow>
      )}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PatientName = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: ${colors.black};
  line-height: 1.3;
`;

const DetailsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
`;

const DetailLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.black50};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DetailValue = styled.span`
  font-size: 14px;
  color: ${colors.black75};
`;

const Age = styled.span`
  color: ${colors.black50};
`;

const Divider = styled.div`
  width: 1px;
  height: 14px;
  background: ${colors.black25};
`;
